<template>
    <div v-if="yandexCaptchaEnabled"
         style="height: 100px; margin-bottom: 10px;"
         :id="containerId"
         class="smart-captcha"
         :data-sitekey="yandexCaptchaKey">
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { yandexCaptchaKey, yandexCaptchaEnabled, lang } from '@/plugins/utils.js'

// Props and emits
const emit = defineEmits(['token-received', 'token-expired'])

// Data
const captchaToken = ref('')
const isTokenValid = ref(false)
const observer = ref(null)
const containerId = ref(`captcha-container-${Math.random().toString(36).substr(2, 9)}`)
let widgetId = ref(null)

// Methods
const reset = (targetWidgetId) => {
  // Сбрасываем внутреннее состояние
  captchaToken.value = ''
  isTokenValid.value = false
  
  // Если есть Yandex SmartCaptcha API, используем его метод reset
  if (window.smartCaptcha && window.smartCaptcha.reset) {
    try {
      if (targetWidgetId !== undefined) {
        window.smartCaptcha.reset(targetWidgetId)
      } else if (widgetId.value !== null) {
        window.smartCaptcha.reset(widgetId.value)
      } else {
        window.smartCaptcha.reset()
      }
      console.log('SmartCaptcha reset successfully')
    } catch (error) {
      console.warn('SmartCaptcha reset error:', error)
      // Fallback - перерендерим весь виджет
      rerenderWidget()
    }
  } else {
    // Fallback - перерендерим весь виджет
    rerenderWidget()
  }
}

const rerenderWidget = () => {
  const container = document.getElementById(containerId.value)
  if (container) {
    container.innerHTML = ''
    // Повторно инициализируем капчу
    setTimeout(() => {
      initCaptcha()
    }, 100)
  }
}

const checkForToken = () => {
  const container = document.getElementById(containerId.value)
  if (container) {
    const tokenInput = container.querySelector('input[name="smart-token"]')
    if (tokenInput && tokenInput.value) {
      const newToken = tokenInput.value
      if (newToken !== captchaToken.value) {
        captchaToken.value = newToken
        isTokenValid.value = true
        emit('token-received', newToken)
        
        // Токен действителен 5 минут, после чего становится недействительным
        setTimeout(() => {
          isTokenValid.value = false
          captchaToken.value = ''
          emit('token-expired')
        }, 5 * 60 * 1000) // 5 минут
      }
    }
  }
}

// Watchers
watch(() => captchaToken.value, (newToken) => {
  if (newToken) {
    // console.log('YandexCaptcha: Token received:', newToken)
  }
})

// Ждем пока скрипт Yandex SmartCaptcha загрузится
const initCaptcha = () => {
      if (window.smartCaptcha && window.smartCaptcha.render) {
        // Очищаем контейнер перед рендером
        const container = document.getElementById(containerId.value)
        if (container) {
          container.innerHTML = ''
          
          // Рендерим капчу заново
          try {
            const renderedWidgetId = window.smartCaptcha.render(container, {
              sitekey: yandexCaptchaKey,
              hl: lang,
              callback: (token) => {
                captchaToken.value = token
                isTokenValid.value = true
                emit('token-received', token)
                
                // Токен действителен 5 минут
                setTimeout(() => {
                  isTokenValid.value = false
                  captchaToken.value = ''
                  emit('token-expired')
                }, 5 * 60 * 1000)
              }
            })
            // Сохраняем ID виджета для возможности его сброса
            if (renderedWidgetId !== undefined) {
              widgetId.value = renderedWidgetId
            }
            console.log('SmartCaptcha rendered successfully, language:', lang, 'widgetId:', renderedWidgetId)
          } catch (error) {
            console.warn('SmartCaptcha render error:', error)
            // Fallback к старому методу
            setupObserver()
          }
        }
      } else {
        // Если API еще не загружен, ждем и пробуем снова
        const attempts = (initCaptcha.attempts || 0) + 1
        initCaptcha.attempts = attempts
        
        if (attempts < 50) { // максимум 5 секунд ожидания
          setTimeout(initCaptcha, 100)
        } else {
          console.warn('SmartCaptcha API not loaded, using fallback method')
          setupObserver()
        }
      }
    }

    const setupObserver = () => {
      // Создаем MutationObserver для отслеживания изменений в DOM (fallback)
      observer.value = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            checkForToken()
          }
        })
      })
      
      const container = document.getElementById(containerId.value)
      if (container) {
        observer.value.observe(container, {
          childList: true,
          subtree: true
        })
      }
      
      // Проверяем токен каждые 500ms
      const interval = setInterval(checkForToken, 500)
      
      onUnmounted(() => {
        clearInterval(interval)
        if (observer.value) {
          observer.value.disconnect()
        }
      })
    }

// Lifecycle  
onMounted(() => {
  // Небольшая задержка для убеждения что DOM готов
  setTimeout(() => {
    // Инициализируем капчу
    initCaptcha()
  }, 100)
})

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})

// Экспортируем методы для использования в родительском компоненте
defineExpose({
  reset
})
</script> 